import { Dayjs } from 'dayjs'
import { createSelector } from 'reselect'

import { add, getOnlyDate, getWeekdaysBetween, getWorkingDaysInWeek, subtract } from '~/astra'
import type { StoreState } from '~/store/store'
import { useStore } from '~/store/store'

export type IdDate = {
    id: string
    date: Dayjs
}

export function toIdDate(date: Dayjs): IdDate {
    return { id: getOnlyDate(date), date }
}

export function toScheduledDateRangeWhere(startDate: Dayjs, endDate: Dayjs) {
    return { gte: getOnlyDate(startDate), lte: getOnlyDate(endDate) } as const
}

export function getWeekFromSelectedDate(selectedDate: Dayjs) {
    return {
        startDate: selectedDate.startOf('isoWeek'),
        endDate: selectedDate.endOf('isoWeek'),
    }
}

export function getWeekIdDatesFromSelectedDate(selectedDate: Dayjs): IdDate[] {
    const daysOfWeek = getWorkingDaysInWeek(selectedDate)

    return daysOfWeek.map(toIdDate)
}

export const selectWeek = createSelector((state: StoreState) => state.appFilters.selectedDate, getWeekFromSelectedDate)

export const selectWeekIdDates = createSelector((state: StoreState) => state.appFilters.selectedDate, getWeekIdDatesFromSelectedDate)

export function useWeek() {
    return useStore(selectWeek)
}

export function useWeekIdDates(): IdDate[] {
    return useStore(selectWeekIdDates)
}

export const selectDateRange = createSelector(
    (state: StoreState) => state.appFilters.selectedDate,
    (state: StoreState) => state.surgeonScheduler.dateRange,
    (selectedDate, dateRange) => {
        if (dateRange === 'm') {
            return {
                startDate: selectedDate.startOf('month'),
                endDate: selectedDate.endOf('month'),
            }
        }

        const startDate = selectedDate.startOf('isoWeek')
        const endDate = add(startDate, Number(dateRange), 'week').startOf('isoWeek')

        return {
            startDate,
            endDate: subtract(endDate, 1, 'day'),
        }
    }
)

export const selectDateRangeDates = createSelector(selectDateRange, ({ startDate, endDate }) => getWeekdaysBetween(startDate, endDate).map(toIdDate))

export const selectThreeMonthTimeWindow = createSelector(
    (state: StoreState) => state.appFilters.selectedDate,
    selectedDate => ({
        startDate: subtract(selectedDate, 1, 'month').startOf('month'),
        endDate: add(selectedDate, 1, 'month').endOf('month'),
    })
)

export function useDateRange() {
    return useStore(selectDateRange)
}

export function useDateRangeDates(): IdDate[] {
    return useStore(selectDateRangeDates)
}

export function useThreeMonthTimeWindow() {
    return useStore(selectThreeMonthTimeWindow)
}
